"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { FaPhone, FaUser, FaUsers } from "react-icons/fa6";
import ProviderAvatar from "@/components/provider-avatar";
import { PresenceIndicator } from "@/components/team-chat/presence-indicator";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PhoneNumberInput from "@/components/ui/phone-number-input";
import { useProviders } from "@/hooks/use-providers";
import { useTimezone } from "@/hooks/use-timezone";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { CallTransferRequest, TransferTargetRingGroup, TransferTargetStaff } from "@/types/call";
import { type StaffPresenceRank, formatTransferPresence } from "./presence-label";
import { callQueryOptions, transferCall } from "@/api/provider/comms/call";
import { teamChatQueryKeys, useOnlineStatusMaps } from "@/query/team-chat";
interface TransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callId: string;
  onTransferInitiated: () => void;
}
const TransferDialog: React.FC<TransferDialogProps> = ({
  open,
  onOpenChange,
  callId,
  onTransferInitiated
}) => {
  const [transferring, setTransferring] = useState(false);
  const [externalNumber, setExternalNumber] = useState("");

  // Presence comes from the shared React Query caches kept fresh by
  // useTeamChatRealtime (mounted in WhisperHeaderButton). For tenants that
  // have CALLING but not WHISPER, that subscription never mounts, so
  // presence would otherwise be stuck at first-fetch (staleTime: Infinity).
  // Refetch on dialog open so those tenants still see current online state.
  const {
    onlineStatusMap,
    presenceMap
  } = useOnlineStatusMaps();
  const {
    providerIdProviderMap,
    isLoading: isProvidersLoading
  } = useProviders();
  const timeZone = useTimezone();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({
        queryKey: teamChatQueryKeys.onlineProviders()
      });
    }
  }, [open, queryClient]);
  const {
    data: targets,
    isLoading,
    isFetching,
    isError
  } = useQuery({
    ...callQueryOptions.transferTargets(),
    enabled: open
  });

  // queryFn throws on a null payload (see callQueryOptions), so any fetch
  // failure surfaces here and does NOT get cached under staleTime. The
  // !isFetching guard avoids firing the toast on reopen-with-cached-error
  // while the background refetch is still in flight — we only want to toast
  // after the fetch has truly settled in an error state.
  useEffect(() => {
    if (open && isError && !isFetching) {
      toast({
        title: "Error",
        description: "Failed to load transfer targets",
        variant: "error"
      });
    }
  }, [open, isError, isFetching]);

  // Reset the external-number input each time the dialog re-opens.
  useEffect(() => {
    if (open) setExternalNumber("");
  }, [open]);

  // Show the loading state while the initial fetch is outstanding OR when a
  // background refresh is running with no cached data yet.
  const loading = isLoading || isFetching && !targets;
  const rankOf = (id: string): StaffPresenceRank => {
    const status = onlineStatusMap.get(id);
    if (status === "ONLINE") return 0;
    if (status === "WITH_CLIENT") return 1;
    if (status === "BUSY") return 2;
    // While the providers cache is loading, hold offline rows in rank 3 so we
    // don't briefly misclassify providers as non-provider Users (rank 4).
    if (isProvidersLoading || providerIdProviderMap.has(id)) return 3;
    return 4;
  };
  const sortedStaff = useMemo(() => {
    const staff = targets?.staff ?? [];
    return [...staff].sort((a, b) => {
      const rankDiff = rankOf(a.id) - rankOf(b.id);
      if (rankDiff !== 0) return rankDiff;
      const lastDiff = a.lastName.localeCompare(b.lastName, undefined, {
        sensitivity: "base"
      });
      if (lastDiff !== 0) return lastDiff;
      return a.firstName.localeCompare(b.firstName, undefined, {
        sensitivity: "base"
      });
    });
    // rankOf depends on the three values below; safe to ignore the hook hint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets?.staff, onlineStatusMap, providerIdProviderMap, isProvidersLoading]);
  const ringGroupOnlineCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const rg of targets?.ringGroups ?? []) {
      let count = 0;
      for (const id of rg.memberIds) {
        if (onlineStatusMap.has(id)) count += 1;
      }
      counts[rg.id] = count;
    }
    return counts;
  }, [targets?.ringGroups, onlineStatusMap]);
  const handleTransfer = async (request: CallTransferRequest) => {
    setTransferring(true);
    try {
      const result = await transferCall(request);
      if (result?.success) {
        onOpenChange(false);
        onTransferInitiated();
      } else {
        toast({
          title: "Transfer failed",
          description: "Could not transfer the call. Please try again.",
          variant: "error"
        });
      }
    } catch {
      toast({
        title: "Transfer failed",
        description: "Could not transfer the call. Please try again.",
        variant: "error"
      });
    } finally {
      setTransferring(false);
    }
  };
  const handleRingGroupTransfer = (rg: TransferTargetRingGroup) => {
    handleTransfer({
      callId,
      transferType: "ring_group",
      targetId: rg.id
    });
  };
  const handleStaffTransfer = (staff: TransferTargetStaff) => {
    handleTransfer({
      callId,
      transferType: "subscriber",
      targetId: staff.id
    });
  };
  const handleExternalTransfer = () => {
    // PhoneNumberInput strips non-digits and caps at 10, so externalNumber
    // should already be exactly 10 digits — but check defensively so a
    // future change to the input component can't silently admit bad data.
    if (!/^\d{10}$/.test(externalNumber)) {
      toast({
        title: "Invalid number",
        description: "Please enter a valid 10-digit phone number",
        variant: "error"
      });
      return;
    }
    handleTransfer({
      callId,
      transferType: "external",
      phoneNumber: externalNumber
    });
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer Call</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {loading ? <div className="flex items-center justify-center py-8">
              <span className="text-body text-foreground-secondary">
                Loading targets...
              </span>
            </div> : <div className="space-y-4">
              {/* Ring Groups */}
              {targets?.ringGroups && targets.ringGroups.length > 0 && <div className="space-y-2">
                  <div className="flex items-center gap-2 text-body-strong text-foreground-secondary">
                    <FaUsers className="h-3.5 w-3.5" />
                    Ring Groups
                  </div>
                  <div className="space-y-1">
                    {targets.ringGroups.map(rg => {
                const onlineCount = ringGroupOnlineCounts[rg.id] ?? 0;
                const total = rg.memberIds.length;
                const memberSummary = onlineCount > 0 ? `${onlineCount} online · ${total} members` : `${total} members`;
                const tooltip = rg.memberNames.join(", ");
                return <button key={rg.id} onClick={() => handleRingGroupTransfer(rg)} disabled={transferring} title={tooltip || undefined} aria-label={`${rg.name} — ${memberSummary}`} className={cx("flex w-full flex-col rounded-lg border border-border px-3 py-2 text-left transition-colors", "hover:bg-hover disabled:opacity-50")}>
                          <span className="text-body-strong text-foreground">
                            {rg.name}
                          </span>
                          <span className="text-caption text-foreground-secondary">
                            {memberSummary}
                          </span>
                        </button>;
              })}
                  </div>
                </div>}

              {/* Staff */}
              {targets?.staff && targets.staff.length > 0 && <div className="space-y-2">
                  <div className="flex items-center gap-2 text-body-strong text-foreground-secondary">
                    <FaUser className="h-3.5 w-3.5" />
                    Staff Members
                  </div>
                  <div className="space-y-1">
                    {sortedStaff.map(s => {
                const rank = rankOf(s.id);
                const status = onlineStatusMap.get(s.id);
                const presence = presenceMap.get(s.id);
                const {
                  label,
                  tooltip,
                  deemphasize
                } = formatTransferPresence({
                  rank,
                  status,
                  presence,
                  firstName: s.firstName,
                  lastName: s.lastName,
                  timeZone
                });
                const fullName = `${s.firstName} ${s.lastName}`.trim();
                return <button key={s.id} onClick={() => handleStaffTransfer(s)} disabled={transferring} title={tooltip || fullName} aria-label={label ? `${fullName} — ${label}` : fullName} className={cx("flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left transition-colors", "hover:bg-hover disabled:opacity-50", deemphasize && "opacity-60")}>
                          <div className="relative h-6 w-6 shrink-0">
                            <PresenceIndicator status={status} size={24}>
                              <ProviderAvatar providerId={s.id} size={24} />
                            </PresenceIndicator>
                          </div>
                          <span className="text-body-strong text-foreground">
                            {fullName}
                          </span>
                          {label && <span className="ml-auto text-caption text-foreground-secondary">
                              {label}
                            </span>}
                        </button>;
              })}
                  </div>
                </div>}

              {/* External Number */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-body-strong text-foreground-secondary">
                  <FaPhone className="h-3.5 w-3.5" />
                  External Number
                </div>
                <div className="flex gap-2">
                  <PhoneNumberInput value={externalNumber} onChange={e => setExternalNumber(e.target.value)} className="flex-1" />
                  <Button variant="primary" onClick={handleExternalTransfer} disabled={transferring || !externalNumber.trim()}>
                    Transfer
                  </Button>
                </div>
              </div>
            </div>}
        </DialogBody>
      </DialogContent>
    </Dialog>;
};
export default TransferDialog;